/**
 * PNR Generator
 * Generates valid-looking PNR codes and booking references for onward tickets
 */

export interface GeneratedPNR {
  pnr: string;
  bookingReference: string;
  airline: string;
  airlineCode: string;
  validUntil: string;
  generatedAt: string;
}

export class PNRGenerator {
  private static readonly AIRLINE_CODES = [
    { code: 'BA', name: 'British Airways' },
    { code: 'LH', name: 'Lufthansa' },
    { code: 'AF', name: 'Air France' },
    { code: 'KL', name: 'KLM' },
    { code: 'EK', name: 'Emirates' },
    { code: 'QR', name: 'Qatar Airways' },
    { code: 'TK', name: 'Turkish Airlines' },
    { code: 'SQ', name: 'Singapore Airlines' },
  ];

  /**
   * Generate a valid-looking PNR code
   * Format: 6 alphanumeric characters (e.g., ABC123, XYZ789)
   */
  static generatePNR(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let pnr = '';
    for (let i = 0; i < 6; i++) {
      pnr += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pnr;
  }

  /**
   * Generate a booking reference
   * Format: 6 alphanumeric characters (same as PNR - industry standard)
   */
  static generateBookingReference(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let ref = '';
    for (let i = 0; i < 6; i++) {
      ref += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return ref;
  }

  /**
   * Select a random airline
   */
  static selectAirline(): { code: string; name: string } {
    return this.AIRLINE_CODES[Math.floor(Math.random() * this.AIRLINE_CODES.length)];
  }

  /**
   * Calculate validity date based on plan
   */
  static calculateValidityDate(plan: 'basic' | 'standard' | 'premium'): string {
    const now = new Date();
    let daysToAdd = 2; // basic: 48 hours

    if (plan === 'standard') {
      daysToAdd = 7;
    } else if (plan === 'premium') {
      daysToAdd = 14;
    }

    now.setDate(now.getDate() + daysToAdd);
    return now.toISOString();
  }

  /**
   * Generate complete PNR details
   */
  static generate(plan: 'basic' | 'standard' | 'premium'): GeneratedPNR {
    const airline = this.selectAirline();
    return {
      pnr: this.generatePNR(),
      bookingReference: this.generateBookingReference(),
      airline: airline.name,
      airlineCode: airline.code,
      validUntil: this.calculateValidityDate(plan),
      generatedAt: new Date().toISOString(),
    };
  }

  /**
   * Format PNR for display
   */
  static formatPNR(pnr: string): string {
    return pnr.match(/.{1,3}/g)?.join('-') || pnr;
  }

  /**
   * Validate PNR format
   */
  static isValidPNR(pnr: string): boolean {
    return /^[A-Z0-9]{6}$/.test(pnr);
  }
}